import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Lock } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import LBLogo from "@/components/LBLogo";

export function LangSwitch({ compact = false }) {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center border border-gold/30 ${compact ? "text-[11px]" : "text-xs"}`} data-testid="lang-switch">
      {["it", "en"].map((l, i) => (
        <button
          key={l}
          data-testid={`lang-switch-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 font-mono uppercase tracking-[0.15em] transition-colors ${
            lang === l ? "bg-gold text-navy font-semibold" : "text-ivory/60 hover:text-gold-bright"
          } ${i === 0 ? "border-r border-gold/30" : ""}`}
          aria-pressed={lang === l}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const { tr } = useLang();
  const nav = tr("nav");
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const links = [
    { to: "/about", label: nav.about, id: "about" },
    { to: "/advisory", label: nav.advisory, id: "advisory" },
    { to: "/knowledge-center", label: nav.knowledge, id: "knowledge" },
    { to: "/insights", label: nav.insights, id: "insights" },
    { to: "/contact", label: nav.contact, id: "contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-navy/85 backdrop-blur-xl border-b border-gold/20" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-4" aria-label="LB Trade Advisory">
          <LBLogo size={44} />
          <span className="hidden flex-col sm:flex">
            <span className="font-serif text-lg leading-tight text-ivory">LB Trade Advisory</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Knowledge. Solutions. Trust.</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-desktop">
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `text-[13px] uppercase tracking-[0.14em] transition-colors ${
                  isActive ? "text-gold-bright" : "text-ivory/70 hover:text-gold-bright"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-5 lg:flex">
          <LangSwitch />
          <Link
            to="/admin"
            data-testid="nav-admin-link"
            className="flex items-center gap-2 border border-ivory/15 px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-ivory/50 transition-colors hover:border-gold/50 hover:text-gold-bright"
          >
            <Lock className="h-3 w-3" /> Admin
          </Link>
        </div>

        <div className="flex items-center gap-4 lg:hidden">
          <LangSwitch compact />
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-11 w-11 items-center justify-center border border-gold/30 text-gold-bright"
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 top-[73px] z-40 flex flex-col justify-between overflow-y-auto bg-navy/[0.98] backdrop-blur-xl lg:hidden"
          >
            <nav className="flex flex-col gap-2 px-6 pt-10">
              {links.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className={({ isActive }) =>
                      `block border-b border-gold/10 py-5 font-serif text-3xl ${
                        isActive ? "italic text-gold-bright" : "text-ivory"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center justify-between px-6 pb-10"
            >
              <Link
                to="/admin"
                data-testid="mobile-admin-link"
                className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ivory/40"
              >
                <Lock className="h-3 w-3" /> {nav.admin}
              </Link>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/70">LB Trade Advisory</span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
